import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Top-level error boundary.
 *
 * Catches any render-time JS exception bubbling up through the React tree
 * and renders a polite fallback card instead of a white screen. Also logs
 * the full stack to the console for debugging.
 *
 * Does NOT catch:
 *  - Async rejections (handled at the call site with .catch / try-catch)
 *  - Errors inside event handlers (wrap those individually)
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = (this.state.error && this.state.error.message) || "Unknown error";

    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen bg-white text-[#0A0A0A] flex items-center justify-center p-6"
      >
        <div className="max-w-lg w-full">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF3333] text-white mb-5">
            <AlertTriangle className="w-7 h-7" strokeWidth={2.25} />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3333] mb-2">
            client · render error
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.02] mb-4">
            Something hiccupped on this page.
          </h1>
          <p className="text-[#4B5563] mb-6 leading-relaxed">
            A transient error prevented this view from rendering. Reloading
            usually fixes it. If you keep seeing this, clear your browser
            cache for <code className="font-mono text-xs bg-[#F8F9FA] px-1.5 py-0.5 border border-[#E5E7EB]">bankdesks.com</code> and try again.
          </p>
          <pre className="font-mono text-[11px] bg-[#F8F9FA] border border-[#E5E7EB] p-3 mb-6 whitespace-pre-wrap break-words text-[#4B5563] max-h-40 overflow-auto">
            {msg}
          </pre>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleReload}
              data-testid="error-reload"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-bold px-4 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#002FA7] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reload page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-home"
              className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Home className="w-3.5 h-3.5" /> Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
